import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "sineann_age_verified";

export default function AgeGate() {
  const [state, setState] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "true" ? "verified" : "pending"
  );

  useEffect(() => {
    document.body.style.overflow = state === "verified" ? "" : "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [state]);

  const confirm = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setState("verified");
  };

  return (
    <AnimatePresence>
      {state !== "verified" && (
        <motion.div
          data-testid="age-gate"
          className="fixed inset-0 z-[100] bg-[#0A0A0A] flex items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6 } }}
        >
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 50% 30%, #F5F5F0 0%, transparent 60%)" }} />
          <motion.div
            className="relative max-w-md w-full text-center"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          >
            <img src="/sineann-emblem.png" alt="Sineann emblem" className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#A8A39D] mb-4">Established 1994 · Estate Grown</p>
            {state === "pending" ? (
              <>
                <h2 className="font-heading text-4xl sm:text-5xl text-[#F5F5F0] mb-4">Before we pour</h2>
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#5C1522]" />
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#5C1522]" />
                  <span className="h-px w-10 bg-[#5C1522]" />
                </div>
                <p className="text-[#A8A39D] text-sm sm:text-base mb-10 leading-relaxed">
                  This site shares information about wine. You must be of legal drinking
                  age in your country of residence to enter.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    data-testid="age-gate-confirm-button"
                    onClick={confirm}
                    className="w-full sm:w-auto px-8 py-3.5 bg-wine text-[#F5F5F0] text-xs tracking-[0.25em] uppercase transition-colors duration-300 hover:bg-wine-hover"
                  >
                    I am 21 or older
                  </button>
                  <button
                    data-testid="age-gate-decline-button"
                    onClick={() => setState("denied")}
                    className="w-full sm:w-auto px-8 py-3.5 border border-[#3a3a38] text-[#A8A39D] text-xs tracking-[0.25em] uppercase transition-colors duration-300 hover:border-[#A8A39D] hover:text-[#F5F5F0]"
                  >
                    Not yet
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="age-gate-denied-message">
                <h2 className="font-heading text-4xl sm:text-5xl text-[#F5F5F0] mb-4">Until then</h2>
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#5C1522]" />
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#5C1522]" />
                  <span className="h-px w-10 bg-[#5C1522]" />
                </div>
                <p className="text-[#A8A39D] text-sm sm:text-base leading-relaxed">
                  We're glad to wait. Please come back and visit us when you're of legal
                  drinking age — the wine will keep.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
